(() => {
  // Section tabs: pin under the header once scrolled past, and mark the section in view.
  if (!customElements.get('cpr-section-tabs')) {
    customElements.define(
      'cpr-section-tabs',
      class CprSectionTabs extends HTMLElement {
        connectedCallback() {
          this.nav = this.querySelector('.cpr-tabs');
          this.spacer = this.querySelector('[data-cpr-tabs-spacer]');
          this.links = Array.from(this.querySelectorAll('[data-cpr-tab]'));
          this.targets = this.links.map((link) => document.getElementById(link.dataset.cprTab));
          this.onScroll = () => this.update();
          window.addEventListener('scroll', this.onScroll, { passive: true });
          window.addEventListener('resize', this.onScroll);
          this.links.forEach((link, i) =>
            link.addEventListener('click', (event) => {
              const target = this.targets[i];
              if (!target) return;
              event.preventDefault();
              const top =
                target.getBoundingClientRect().top + window.scrollY - this.offset() - this.nav.offsetHeight + 1;
              window.scrollTo({
                top,
                behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
              });
              window.history.replaceState({}, '', `#${link.dataset.cprTab}`);
            }),
          );
          this.update();
        }

        disconnectedCallback() {
          window.removeEventListener('scroll', this.onScroll);
          window.removeEventListener('resize', this.onScroll);
        }

        // Bottom edge of a sticky/fixed header, so the bar sits right under it.
        offset() {
          const header = document.querySelector(
            '.shopify-section-group-header-group .cx-header, sticky-header, .section-header',
          );
          if (!header) return 0;
          const style = getComputedStyle(header);
          const pinned = style.position === 'fixed' || style.position === 'sticky';
          const rect = header.getBoundingClientRect();
          return pinned && rect.bottom > 0 ? Math.max(0, rect.bottom) : 0;
        }

        update() {
          const offset = this.offset();
          const pinned = this.getBoundingClientRect().top < offset;
          this.classList.toggle('is-pinned', pinned);
          this.style.setProperty('--cpr-tabs-top', `${offset}px`);
          if (this.spacer) this.spacer.style.height = `${this.nav.offsetHeight}px`;
          const line = offset + this.nav.offsetHeight + 40;
          let active = -1;
          this.targets.forEach((target, i) => {
            if (target && target.getBoundingClientRect().top <= line) active = i;
          });
          this.links.forEach((link, i) => {
            if (i === active) link.setAttribute('aria-current', 'true');
            else link.removeAttribute('aria-current');
          });
        }
      },
    );
  }

  // The making: play the film in place; on mobile, dots follow the card carousel.
  if (!customElements.get('cpr-making')) {
    customElements.define(
      'cpr-making',
      class CprMaking extends HTMLElement {
        connectedCallback() {
          this.querySelector('[data-cpr-making-play]')?.addEventListener('click', () => {
            const template = this.querySelector('[data-cpr-making-video]');
            const player = this.querySelector('[data-cpr-making-player]');
            if (!template || !player) return;
            player.appendChild(template.content.cloneNode(true));
            this.classList.add('is-playing');
            const video = player.querySelector('video');
            video?.play?.().catch(() => {});
            video?.focus();
          });
          const cards = this.querySelector('[data-cpr-cards]');
          const dots = Array.from(this.querySelectorAll('[data-cpr-dot]'));
          const count = this.querySelector('[data-cpr-chapter]');
          cards?.addEventListener(
            'scroll',
            () => {
              const items = Array.from(cards.children);
              const left = cards.getBoundingClientRect().left;
              let index = 0;
              items.forEach((item, i) => {
                if (item.getBoundingClientRect().left - left < item.offsetWidth / 2) index = i;
              });
              dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
              if (count) count.textContent = index + 1;
            },
            { passive: true },
          );
        }
      },
    );
  }

  // Delivery dates: refresh [date] / [weeks] with today's date in the visitor's browser.
  const fillDates = () => {
    document.querySelectorAll('[data-cpr-journey]').forEach((section) => {
      const days = parseInt(section.dataset.days || '0', 10);
      if (!days) return;
      const date = new Date();
      date.setDate(date.getDate() + days);
      const text = date.toLocaleDateString(document.documentElement.lang || undefined, {
        month: 'long',
        day: 'numeric',
      });
      section.querySelectorAll('[data-cpr-date-template]').forEach((el) => {
        el.textContent = el.dataset.cprDateTemplate
          .replace(/\[date\]/g, text)
          .replace(/\[weeks\]/g, Math.round(days / 7));
      });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fillDates);
  else fillDates();
})();

(() => {
  // Reviews: photo filter, "read more" paging, photo rail and the photo lightbox.
  if (customElements.get('cpr-reviews')) return;
  customElements.define(
    'cpr-reviews',
    class CprReviews extends HTMLElement {
      connectedCallback() {
        this.pageSize = parseInt(this.dataset.pageSize || '4', 10);
        this.filter = 'all';
        this.limit = this.pageSize;
        this.querySelectorAll('[data-cpr-rv-filter]').forEach((chip) =>
          chip.addEventListener('click', () => {
            this.filter = chip.dataset.cprRvFilter;
            this.limit = this.pageSize;
            this.querySelectorAll('[data-cpr-rv-filter]').forEach((c) =>
              c.setAttribute('aria-pressed', c === chip ? 'true' : 'false'),
            );
            const photos = this.querySelector('.cpr-rv__photos');
            if (photos && this.photoCount() > 0) photos.hidden = false;
            this.render();
          }),
        );
        this.querySelector('[data-cpr-rv-more]')?.addEventListener('click', () => this.more());
        if (this.dataset.source === 'judgeme') {
          this.initJudgeme();
          return;
        }
        this.rows = Array.from(this.querySelectorAll('[data-cpr-rv-row]'));
        this.bindRail();
        this.bindLightbox();
        this.render();
      }

      photoCount() {
        return this.querySelectorAll('[data-cpr-rv-rail] [data-cpr-rv-open]').length;
      }

      async more() {
        this.limit += this.pageSize;
        const matching = this.matching();
        if (this.judgeme && matching.length < this.limit && this.judgeme.hasMore) await this.loadJudgemePage();
        this.render();
      }

      matching() {
        return this.rows.filter((row) => this.filter === 'all' || row.dataset.hasPhoto === 'true');
      }

      render() {
        const matching = this.matching();
        this.rows.forEach((row) => (row.hidden = true));
        matching.slice(0, this.limit).forEach((row) => (row.hidden = false));
        const empty = this.querySelector('[data-cpr-rv-empty]');
        if (empty) empty.hidden = matching.length > 0 || !!this.judgeme?.loading;
        const more = this.querySelector('[data-cpr-rv-more]');
        if (more) more.hidden = matching.length <= this.limit && !(this.judgeme?.hasMore && this.filter === 'all');
        const showing = this.querySelector('[data-cpr-rv-showing]');
        if (showing) {
          const total = this.filter === 'all' ? showing.dataset.total : matching.length;
          showing.textContent = showing.dataset.cprRvShowing
            .replace('[shown]', Math.min(this.limit, matching.length))
            .replace('[total]', total);
        }
      }

      /* ---------- Judge.me ---------- */
      // Reviews come from Judge.me's public widget endpoint (the same one its own widget uses),
      // parsed from its HTML and rendered with this section's markup.
      initJudgeme() {
        this.rows = [];
        // Customer photo blocks are already in the rail and lightbox; review photos are numbered after them.
        const customCount = parseInt(this.querySelector('[data-cpr-rv-photos]')?.dataset.customCount || '0', 10);
        this.judgeme = { page: 0, hasMore: true, loading: false, seen: new Set(), photos: new Array(customCount).fill(null) };
        this.list = this.querySelector('[data-cpr-rv-list]');
        const template = this.querySelector('[data-cpr-rv-lightbox-template]');
        if (template) this.append(template.content.cloneNode(true));
        this.bindRail();
        this.bindLightbox();
        this.loadJudgemePage().then(() => this.render());
      }

      judgemeUrl(page) {
        const domain = encodeURIComponent(this.dataset.shopDomain);
        const perPage = Math.max(this.pageSize * 2, 10);
        const base = `https://api.judge.me/reviews/${this.dataset.judgemeScope === 'store' ? 'all_reviews_js_based' : 'reviews_for_widget'}`;
        let url = `${base}?url=${domain}&shop_domain=${domain}&platform=shopify&page=${page}&per_page=${perPage}`;
        if (this.dataset.judgemeScope !== 'store') url += `&product_id=${encodeURIComponent(this.dataset.productId)}`;
        return url;
      }

      async loadJudgemePage() {
        const state = this.judgeme;
        if (state.loading || !state.hasMore) return;
        state.loading = true;
        try {
          const response = await fetch(this.judgemeUrl(state.page + 1));
          const data = await response.json();
          const html = data.html || '';
          const reviews = this.parseJudgeme(html).filter((review) => {
            if (state.seen.has(review.id)) return false;
            state.seen.add(review.id);
            return true;
          });
          state.page += 1;
          state.hasMore = reviews.length > 0 && /jdgm-rev\b/.test(html);
          reviews.forEach((review) => this.addReview(review));
          this.querySelector('[data-cpr-rv-toolbar]')?.removeAttribute('hidden');
          this.refreshPhotos();
        } catch (error) {
          state.hasMore = false;
        } finally {
          state.loading = false;
        }
      }

      parseJudgeme(html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return Array.from(doc.querySelectorAll('.jdgm-rev')).map((el) => {
          const text = (selector) => el.querySelector(selector)?.textContent.replace(/\s+/g, ' ').trim() || '';
          const photos = Array.from(el.querySelectorAll('.jdgm-rev__pic-link, [data-mfp-src]'))
            .map((link) => {
              const img = link.querySelector('img');
              const full = link.getAttribute('data-mfp-src') || link.getAttribute('href') || '';
              const thumb = img?.getAttribute('data-src') || img?.getAttribute('src') || full;
              return full ? { full, thumb } : null;
            })
            .filter(Boolean);
          return {
            id: el.getAttribute('data-review-id') || text('.jdgm-rev__body'),
            author: text('.jdgm-rev__author'),
            rating: parseInt(el.querySelector('.jdgm-rev__rating')?.getAttribute('data-score') || '5', 10),
            title: text('.jdgm-rev__title'),
            body: text('.jdgm-rev__body'),
            date: el.querySelector('.jdgm-rev__timestamp')?.getAttribute('data-content') || '',
            verified: el.getAttribute('data-verified-buyer') === 'true',
            product: text('.jdgm-rev__prod-link'),
            productUrl: el.querySelector('.jdgm-rev__prod-link')?.getAttribute('href')?.split('#')[0] || '',
            photos,
          };
        });
      }

      relativeDate(value) {
        const then = Date.parse(value.replace(' UTC', 'Z').replace(' ', 'T'));
        if (Number.isNaN(then)) return '';
        const days = Math.floor((Date.now() - then) / 86400000);
        const format = new Intl.RelativeTimeFormat(this.dataset.locale || document.documentElement.lang || 'en', {
          numeric: 'auto',
        });
        if (days < 7) return format.format(-days, 'day');
        if (days < 30) return format.format(-Math.floor(days / 7), 'week');
        if (days < 365) return format.format(-Math.floor(days / 30), 'month');
        return format.format(-Math.floor(days / 365), 'year');
      }

      el(tag, className, text) {
        const node = document.createElement(tag);
        if (className) node.className = className;
        if (text) node.textContent = text;
        return node;
      }

      stars(rating) {
        const wrap = this.el('span', 'cpr-rv__stars');
        wrap.setAttribute('role', 'img');
        wrap.setAttribute('aria-label', `${rating} / 5`);
        for (let i = 1; i <= 5; i++) {
          if (i <= rating) wrap.append('★');
          else wrap.append(this.el('span', 'cpr-rv__stars-off', '★'));
        }
        return wrap;
      }

      addReview(review) {
        const row = this.el('article', 'cpr-rv__row');
        row.dataset.cprRvRow = '';
        row.dataset.hasPhoto = review.photos.length ? 'true' : 'false';
        row.hidden = true;
        const meta = this.el('div', 'cpr-rv__meta');
        meta.append(this.el('span', 'cpr-rv__author', review.author), this.stars(review.rating));
        const when = this.relativeDate(review.date);
        if (when) meta.append(this.el('span', 'cpr-rv__date', when));
        const body = this.el('div', 'cpr-rv__body');
        body.append(this.el('p', 'cpr-rv__quote', `“${review.body || review.title}”`));
        if (this.dataset.judgemeScope === 'store' && review.product) {
          const product = this.el('p', 'cpr-rv__product');
          const label = this.dataset.productLabel;
          if (label) product.append(`${label} `);
          const link = this.el('a', null, review.product);
          if (review.productUrl) link.href = review.productUrl;
          product.append(link);
          body.append(product);
        }
        review.photos.forEach((photo, i) => {
          const index = this.addPhoto(review, photo);
          if (i > 0) return;
          const shot = this.el('button', 'cpr-rv__shot');
          shot.type = 'button';
          shot.dataset.cprRvOpen = index;
          shot.setAttribute('aria-label', review.author);
          const img = this.el('img');
          img.src = photo.thumb;
          img.alt = '';
          img.loading = 'lazy';
          img.width = 180;
          img.height = 120;
          shot.append(img);
          body.append(shot);
          this.bindOpen(shot);
        });
        row.append(meta, body);
        this.list.insertBefore(row, this.querySelector('[data-cpr-rv-empty]'));
        this.rows.push(row);
      }

      addPhoto(review, photo) {
        const index = this.judgeme.photos.length;
        this.judgeme.photos.push(photo);
        const rail = this.querySelector('[data-cpr-rv-rail]');
        if (rail) {
          const tile = this.el('button', 'cpr-rv__tile');
          tile.type = 'button';
          tile.dataset.cprRvOpen = index;
          tile.setAttribute('aria-label', review.author);
          const frame = this.el('span', 'cpr-rv__tile-frame');
          const img = this.el('img');
          img.src = photo.thumb;
          img.alt = '';
          img.loading = 'lazy';
          img.draggable = false;
          frame.append(img, this.el('span', 'cpr-rv__tile-ring'));
          tile.append(frame, this.el('span', 'cpr-rv__tile-stars', '★'.repeat(review.rating)));
          rail.append(tile);
          this.bindOpen(tile);
        }
        const thumbs = this.querySelector('[data-cpr-rv-lb-thumbs]');
        if (thumbs) {
          const thumb = this.el('button', 'cpr-rv__lightbox-thumb');
          thumb.type = 'button';
          thumb.dataset.cprRvThumb = index;
          thumb.dataset.src = photo.full;
          thumb.dataset.author = review.author;
          thumb.dataset.quote = `“${review.body}”`;
          thumb.dataset.rating = review.rating;
          thumb.dataset.verified = review.verified ? 'true' : 'false';
          thumb.setAttribute('aria-label', `${index + 1}`);
          const img = this.el('img');
          img.src = photo.thumb;
          img.alt = '';
          img.loading = 'lazy';
          thumb.append(img);
          thumb.addEventListener('click', () => this.show(index));
          thumbs.append(thumb);
          this.thumbs = Array.from(thumbs.children);
        }
        return index;
      }

      refreshPhotos() {
        const count = this.judgeme.photos.length;
        const reviewPhotos = this.rows.filter((row) => row.dataset.hasPhoto === 'true').length;
        const photos = this.querySelector('[data-cpr-rv-photos]');
        if (photos) photos.hidden = count === 0;
        const label = this.querySelector('[data-cpr-rv-photos-label]');
        if (label) label.textContent = label.dataset.cprRvPhotosLabel.replace('[count]', count);
        this.querySelectorAll('[data-cpr-rv-photo-total]').forEach((el) => (el.textContent = count));
        this.querySelectorAll('[data-cpr-rv-photo-count]').forEach((el) => (el.textContent = reviewPhotos));
        const chip = this.querySelector('[data-cpr-rv-filter="photos"]');
        if (chip) chip.hidden = reviewPhotos === 0;
        this.updateRail?.();
      }

      /* ---------- Photo rail ---------- */
      bindRail() {
        const rail = this.querySelector('[data-cpr-rv-rail]');
        if (!rail) return;
        const prev = this.querySelector('[data-cpr-rv-rail-prev]');
        const next = this.querySelector('[data-cpr-rv-rail-next]');
        const fadeL = this.querySelector('[data-cpr-rv-fade-l]');
        const fadeR = this.querySelector('[data-cpr-rv-fade-r]');
        const update = () => {
          const max = rail.scrollWidth - rail.clientWidth - 1;
          prev?.setAttribute('aria-disabled', rail.scrollLeft <= 0 ? 'true' : 'false');
          next?.setAttribute('aria-disabled', rail.scrollLeft >= max ? 'true' : 'false');
          if (fadeL) fadeL.hidden = rail.scrollLeft <= 0;
          if (fadeR) fadeR.hidden = rail.scrollLeft >= max;
        };
        this.updateRail = update;
        const step = (dir) => {
          const tile = rail.firstElementChild;
          const width = tile
            ? tile.getBoundingClientRect().width + parseFloat(getComputedStyle(rail).columnGap || '0')
            : rail.clientWidth;
          rail.scrollBy({ left: dir * width * Math.max(1, Math.floor(rail.clientWidth / width)), behavior: 'smooth' });
        };
        prev?.addEventListener('click', () => prev.getAttribute('aria-disabled') !== 'true' && step(-1));
        next?.addEventListener('click', () => next.getAttribute('aria-disabled') !== 'true' && step(1));
        rail.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update);
        update();
      }

      /* ---------- Lightbox ---------- */
      bindOpen(button) {
        button.addEventListener('click', () => this.open(parseInt(button.dataset.cprRvOpen, 10), button));
      }

      bindLightbox() {
        this.lightbox = this.querySelector('[data-cpr-rv-lightbox]');
        if (!this.lightbox) return;
        this.thumbs = Array.from(this.lightbox.querySelectorAll('[data-cpr-rv-thumb]'));
        this.querySelectorAll('[data-cpr-rv-open]').forEach((button) => this.bindOpen(button));
        this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.show(i)));
        this.lightbox.querySelector('[data-cpr-rv-lb-close]').addEventListener('click', () => this.close());
        this.lightbox.querySelector('[data-cpr-rv-lb-prev]').addEventListener('click', () => this.show(this.index - 1));
        this.lightbox.querySelector('[data-cpr-rv-lb-next]').addEventListener('click', () => this.show(this.index + 1));
        this.lightbox.addEventListener('click', (event) => {
          if (event.target === this.lightbox) this.close();
        });
        this.lightbox.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') this.close();
          if (event.key === 'ArrowLeft') this.show(this.index - 1);
          if (event.key === 'ArrowRight') this.show(this.index + 1);
        });
      }

      open(index, opener) {
        if (!this.lightbox || !this.thumbs?.length) return;
        this.opener = opener;
        this.lightbox.hidden = false;
        document.documentElement.style.overflow = 'hidden';
        this.show(index);
        this.lightbox.querySelector('[data-cpr-rv-lb-close]').focus();
      }

      show(index) {
        const count = this.thumbs.length;
        if (!count) return;
        this.index = ((index % count) + count) % count;
        const thumb = this.thumbs[this.index];
        const img = this.lightbox.querySelector('[data-cpr-rv-lb-img]');
        img.src = thumb.dataset.src;
        img.alt = thumb.getAttribute('aria-label');
        this.lightbox.querySelector('[data-cpr-rv-lb-byline]').textContent = thumb.dataset.author;
        this.lightbox.querySelector('[data-cpr-rv-lb-quote]').textContent = thumb.dataset.quote;
        this.lightbox.querySelector('[data-cpr-rv-lb-stars]').textContent = '★'.repeat(
          parseInt(thumb.dataset.rating || '5', 10),
        );
        const verified = this.lightbox.querySelector('[data-cpr-rv-lb-verified]');
        if (verified) verified.hidden = thumb.dataset.verified === 'false';
        this.lightbox.querySelector('[data-cpr-rv-lb-counter]').textContent = `${this.index + 1} / ${count}`;
        this.thumbs.forEach((t, i) =>
          i === this.index ? t.setAttribute('aria-current', 'true') : t.removeAttribute('aria-current'),
        );
      }

      close() {
        this.lightbox.hidden = true;
        document.documentElement.style.overflow = '';
        this.opener?.focus();
      }
    },
  );
})();
